/**
 * posAudio — global AudioContext for the POS shell.
 *
 * Chromium (and therefore the Android WebView) requires that AudioContext
 * be created OR resumed inside a user gesture. Poll-triggered playback is
 * NOT a gesture — so if we lazy-init the context inside the notification
 * poller, `state` stays "suspended" forever and no sound ever plays.
 *
 * This module exports:
 *   - unlock() : safe to call from any user gesture (PIN pad tap, tab
 *                click, first touch). Creates the context if needed and
 *                calls resume(). Records the result in posDiagnostics.
 *   - chime()  : plays the two-tone alert. Never throws. If the context
 *                is still suspended, records SoundState=BLOCKED and
 *                returns silently — MUST NOT propagate to the caller
 *                (see req 8: sound must never take down auto-print).
 */
import { setSoundState, recordChime } from './posDiagnostics';

let ctx = null;

const ensureContext = () => {
  if (ctx) return ctx;
  const AC = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
  if (!AC) return null;
  try {
    ctx = new AC();
    return ctx;
  } catch (_err) {
    setSoundState('ERROR');
    return null;
  }
};

/**
 * Called from a user gesture. Creates the AudioContext if it doesn't
 * exist and resumes it if it's suspended. Safe to call repeatedly.
 */
export const unlock = () => {
  const c = ensureContext();
  if (!c) return;
  if (c.state === 'suspended') {
    // Chromium: resume() returns a Promise. It resolves in-gesture only.
    c.resume()
      .then(() => setSoundState(c.state === 'running' ? 'READY' : 'BLOCKED'))
      .catch(() => setSoundState('BLOCKED'));
  } else if (c.state === 'running') {
    setSoundState('READY');
  } else {
    setSoundState('BLOCKED');
  }
};

const CHIME_DURATION_MS = 5000;   // total attention window
const CHIME_CADENCE_MS = 900;     // gap between two-tone beeps within a cycle

let chimeActive = false;          // true while a 5s cycle is currently playing
let chimePendingRepeat = false;   // a new order arrived mid-cycle — play another cycle after

const playSingleTwoTone = (c, atOffset) => {
  const now = c.currentTime + atOffset;
  const limiter = c.createDynamicsCompressor();
  limiter.threshold.value = -8;
  limiter.knee.value = 6;
  limiter.ratio.value = 12;
  limiter.attack.value = 0.003;
  limiter.release.value = 0.1;
  limiter.connect(c.destination);

  const playBeep = (freq, gain, startAt) => {
    const oscSine = c.createOscillator();
    const oscSquare = c.createOscillator();
    const g = c.createGain();
    oscSine.type = 'sine';
    oscSine.frequency.value = freq;
    oscSquare.type = 'square';
    oscSquare.frequency.value = freq * 2;
    const t = now + startAt;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(gain, t + 0.015);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
    oscSine.connect(g);
    oscSquare.connect(g);
    g.connect(limiter);
    oscSine.start(t);
    oscSquare.start(t);
    oscSine.stop(t + 0.4);
    oscSquare.stop(t + 0.4);
  };
  playBeep(987.77, 0.65, 0);
  playBeep(1318.5, 0.65, 0.45);
};

/**
 * Play a ~5-second attention alert made of repeating two-tone chimes.
 *
 * Concurrency contract (req 7 + 8):
 *   • If a cycle is already in progress, DON'T start a second overlapping
 *     stream. Instead flag chimePendingRepeat — a fresh 5s cycle will
 *     kick off as soon as the current one finishes, so a new order that
 *     lands mid-alert still gets its own attention window.
 *   • Fully swallows all exceptions — an audio failure must NEVER take
 *     down auto-print (req 8).
 */
export const chime = () => {
  try {
    const c = ensureContext();
    if (!c) { setSoundState('ERROR'); return; }
    if (c.state === 'suspended') { c.resume().catch(() => {}); }
    if (c.state !== 'running') {
      setSoundState('BLOCKED');
      // eslint-disable-next-line no-console
      console.warn('[SOUND] Playback blocked — AudioContext state=', c.state);
      return;
    }
    setSoundState('READY');

    if (chimeActive) {
      // Another order landed mid-cycle — queue exactly one repeat.
      chimePendingRepeat = true;
      // eslint-disable-next-line no-console
      console.log('[SOUND] Chime already active — queued repeat');
      return;
    }

    chimeActive = true;
    // eslint-disable-next-line no-console
    console.log('[SOUND] 5s attention chime started');

    // Schedule beeps every CHIME_CADENCE_MS across CHIME_DURATION_MS.
    // All beeps are scheduled up-front on the Web Audio timeline so
    // Chromium's background-tab throttling can't gap them out.
    let offset = 0;
    while (offset < CHIME_DURATION_MS / 1000) {
      playSingleTwoTone(c, offset);
      offset += CHIME_CADENCE_MS / 1000;
    }
    recordChime();

    // Release the mutex after the audio window closes, and optionally
    // repeat if chime() was called again mid-cycle.
    setTimeout(() => {
      chimeActive = false;
      // eslint-disable-next-line no-console
      console.log('[SOUND] Chime cycle ended');
      if (chimePendingRepeat) {
        chimePendingRepeat = false;
        // eslint-disable-next-line no-console
        console.log('[SOUND] Playing queued repeat');
        chime();
      }
    }, CHIME_DURATION_MS + 100);
  } catch (err) {
    // Belt-and-braces — chime MUST NOT throw upstream.
    chimeActive = false;
    chimePendingRepeat = false;
    setSoundState('ERROR');
    // eslint-disable-next-line no-console
    console.warn('[SOUND] chime threw:', err?.message || err);
  }
};

export const getState = () => (ctx ? ctx.state : 'no-context');
